import React, { HTMLAttributes, Ref, useCallback, useState } from 'react';
import { classNames } from '../../utilities';
import { CheckboxGroupContext } from '.';
import { Icon } from '../Icon';
import '../../base.scss';
import './Checkbox.scss';

export interface CheckboxProps extends HTMLAttributes<HTMLInputElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** Id that points to the label for the checkbox */
  'aria-labelledby'?: string;
  /** Controlled checkbox state, mutually exclusive with defaultChecked. */
  checked?: boolean;
  /** Attach a custom class to the checkbox container */
  className?: string;
  /** A ref to access the component */
  componentRef?: Ref<HTMLInputElement>;
  /** Uncontrolled checkbox state, mutually exclusive with checked */
  defaultChecked?: boolean;
  /** Disable the checkbox */
  disabled?: boolean;
  /** ID for the checkbox */
  id?: string;
  /** Label for the checkbox */
  label?: React.ReactNode | string;
  /** Name for the checkbox */
  name?: string;
  /** Form value */
  value?: string | number;
  /** Callback handler for value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, isChecked?: boolean) => void;
  /** A value referencing the number of items related to the label value */
  count?: number;
}

/** Single Checkbox component */
export function Checkbox(props: CheckboxProps) {
  const { checked, className, defaultChecked, disabled, id, label, name, value, onChange, count } = props;

  // set up a hook for each checkbox to use a possible checkbox group context
  const checkboxGroup = React.useContext(CheckboxGroupContext);

  if (defaultChecked !== undefined && defaultChecked !== null && checked !== undefined && checked !== null) {
    throw new Error('Checkboxes should either be controlled (checked) or uncontrolled (defaultChecked).');
  }

  // setup uncontrolled state
  const [uncontrolledCheckedState, setUncontrolledCheckedState] = useState(
    defaultChecked ?? checkboxGroup.defaultChecked
  );

  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newVal = event.target.checked ?? false;

      // set uncontrolled state
      if (checked === undefined || checked === null) {
        setUncontrolledCheckedState(newVal);
      }

      // callback prop specific to this checkbox
      if (onChange) {
        onChange(event, newVal);
      }

      // callback for a possible CheckboxGroup
      if (checkboxGroup.onChange) {
        checkboxGroup.onChange(event);
      }
    },
    [checked, onChange, checkboxGroup]
  );

  const isDisabled = disabled ?? checkboxGroup.disabled;

  const checkboxClassNames = classNames('ps-checkbox', className, isDisabled ? 'ps-checkbox--disabled' : '');

  return (
    <label className={checkboxClassNames}>
      <span className="ps-checkbox__box">
        <input
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          checked={checked ?? uncontrolledCheckedState}
          className="ps-checkbox__box-input"
          disabled={disabled ?? checkboxGroup.disabled}
          id={id}
          name={name ?? checkboxGroup.name}
          type="checkbox"
          onChange={onChangeWrapper}
          value={value}
        />
        <span className="ps-checkbox__box-icon">
          <Icon name="Check" />
        </span>
      </span>
      <span className="ps-checkbox__label">{label}</span>
      {count != null && <span className="ps-checkbox__count">{count}</span>}
    </label>
  );
}
